<template>
    <div
        v-resize-callback="repositionBackground"
        class="select-button-group d-inline-flex padding-xsmall"
    >
        <app-button
            v-for="(key, label) in options"
            :key="key"
            :ref="key"
            bg="none"
            :color="value === key ? 'var(--lowest-contrast)' : 'var(--highest-contrast)'"
            @click="select(key)"
        >
            {{ label }}
        </app-button>
        <transition name="fade">
            <div
                v-if="value !== undefined"
                ref="background"
                class="background"
            ></div>
        </transition>
    </div>
</template>

<script>
export default {
    name: "SelectButtonGroup",
    props: {
        /**
         * map of label -> value
         */
        options: {
            type: Object,
            required: true,
        },
        /**
         * @model
         * @type any
         */
        value: undefined,
        /**
         * if true the selected value can be unselected by pressing the selected button again
         */
        enableUnselect: {
            type: Boolean,
            default: false
        },
    },
    watch: {
        value: {
            handler() {
                this.$nextTick(() => this.repositionBackground());
            },
        }
    },
    methods: {
        select(newValue) {
            if (this.value === newValue) {
                if (!this.enableUnselect) {
                    return;
                }
                newValue = undefined;
            }
            this.$emit("input", newValue);
        },
        repositionBackground() {
            if (this.value === undefined || !this.$refs[this.value]) {
                return;
            }
            const element = this.$refs[this.value][0].$el;
            this.$refs.background.style.height = `${element.offsetHeight}px`;
            this.$refs.background.style.width = `${element.offsetWidth}px`;
            this.$refs.background.style.top = `${element.offsetTop}px`;
            this.$refs.background.style.left = `${element.offsetLeft}px`;
        },
    }
};
</script>

<style lang="scss" scoped>
.select-button-group {
    position: relative;
    background: $lowest-contrast;
    border-radius: 2rem;
    padding: .3rem;

    .button {
        position: relative;
        z-index: 1;
        transition: color .2s linear
    }

    .background {
        position: absolute;
        transition: opacity .2s linear, top .2s ease-in-out, left .2s ease-in-out, width .2s linear;
        background: $highest-contrast;
        border-radius: 2rem;
    }
}
</style>

<docs>
# Select Button Group

Button group which can be used to select one of a given set of options.

### Default
```vue
<template>
    <div>
        <p>Selected: {{ selected }}</p>
        <select-button-group
            v-model="selected"
            :options="options"
        />
    </div>
</template>
<script>
export default {
    data() {
        return {
            selected: 1,
            options: {
                "First option": 1,
                "Second option": 2,
                "Third option": 3,
            }
        };
    },
}
</script>
```

### Enable unselect
```vue
<template>
    <div>
        <p>Selected: {{ selected }}</p>
        <select-button-group
            v-model="selected"
            :options="options"
            enable-unselect
        />
    </div>
</template>
<script>
    export default {
        data() {
            return {
                selected: 1,
                options: {
                    "First option": 1,
                    "Second option": 2,
                    "Third option": 3,
                }
            };
        },
    }
</script>
```
</docs>
